import {Section, SectionItem} from 'common/components/items-menu/types';
import {MATH_TYPE, PRIMATIVE_MATH_TYPES} from 'common/report/constants';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {Bucket} from 'common/widgets/bucket-pane/types';
import {Expression} from 'common/widgets/cohorts-query-builder/models/types/expression-types';
import {SerializedBehaviorDictionary} from 'common/widgets/cohorts-query-builder/models/types/serialized-cohort-types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {ResourceType} from 'common/widgets/property-filter-menu/models/types'; // TODO (safdar): move this type either here or in a common dir
import {EventItem} from 'common/widgets/query-block/events-menu/types';
import {PropertyItem} from 'common/widgets/query-block/properties-menu/types';
import {Segment} from 'common/widgets/query-block/segment-menu/types';

export type EventFilters = PropertyFilter[];

export enum SelectorTypeDefinitions {
  Behavior = `behavior`,
  EventLaunch = `event_launch`,
  EventPropertyAggregation = `event_property_aggregation`,
  FlowsEvent = `flows_event`,
  UserPropertyAggregation = `user_property_aggregation`,
  GroupedEvent = `grouped_event`,
  Filter = `filter`,
  Segment = `segment`,
}

/** TODO(pablo): remove union with ResourceType and flatten this list */
export type SelectorType = ResourceType | SelectorTypeDefinitions;

export const SelectorTypes: SelectorType[] = [
  ResourceType.Cohort,
  ResourceType.Event,
  ResourceType.User,
  SelectorTypeDefinitions.Behavior,
  SelectorTypeDefinitions.EventLaunch,
  SelectorTypeDefinitions.EventPropertyAggregation,
  SelectorTypeDefinitions.FlowsEvent,
  SelectorTypeDefinitions.UserPropertyAggregation,
  SelectorTypeDefinitions.GroupedEvent,
];

export interface RowTypeComplexOption extends SectionItem {
  value: SelectorType;
  readOnlyEvent?: EventItem;
  includeDateRange?: boolean;
}

/** A RowType represents what kind of entries can be added to the list */
export type RowTypeOption = SelectorType | RowTypeComplexOption;

/** TODO(pablo): remove type based helpers once we can remove `event`
 * for representing all value selections in an entry
 */
export function isEventBasedType(type: SelectorType): boolean {
  const propertyTypes = [
    ResourceType.Event,
    SelectorTypeDefinitions.Behavior,
    SelectorTypeDefinitions.EventLaunch,
    SelectorTypeDefinitions.EventPropertyAggregation,
    SelectorTypeDefinitions.FlowsEvent,
  ] as SelectorType[];

  return propertyTypes.includes(type || ResourceType.Event);
}

export function isUserBasedType(type: SelectorType): boolean {
  const propertyTypes = [ResourceType.User, SelectorTypeDefinitions.UserPropertyAggregation] as SelectorType[];

  return propertyTypes.includes(type);
}

export function isPropertySelectorType(type: SelectorType): type is QueryPropertyEntry['type'] {
  const propertyTypes = [SelectorTypeDefinitions.Filter, SelectorTypeDefinitions.Segment] as SelectorType[];
  return propertyTypes.includes(type);
}

export function canPerformPropertyAggregation(type: SelectorType): boolean {
  const types = [
    SelectorTypeDefinitions.EventPropertyAggregation,
    SelectorTypeDefinitions.UserPropertyAggregation,
  ] as SelectorType[];

  return types.includes(type);
}

export enum FiltersOperator {
  And = `and`,
  Or = `or`,
  OrAny = `or_any`,
  AndAny = `and_any`,
  Then = `then`, // [shawn] Special-case for support of funnels queries within the cohorts-query-builder
}

export interface SavedCohortItem {
  resourceType?: ResourceType.Cohort;
  label: string;
  renamed?: string;
  // "value" is the cohort's id
  value: number;
  data_group_id?: string;
}

interface InlineCohortItem {
  resourceType?: ResourceType.Cohort;
  label: string;
  value: {
    cohort: {
      name: string;
      raw_cohort: {
        name: string;
        behaviors: SerializedBehaviorDictionary;
        selector: Expression;
        data_group_id?: string;
      };
    };
  };
}
export type CohortItem = SavedCohortItem | InlineCohortItem;

export function isInlineCohortItem(item: CohortItem): item is InlineCohortItem {
  return item.value && (item as InlineCohortItem).value.cohort !== undefined;
}

export interface BaseEntry {
  dataGroupId?: string;

  /** Set of filter clauses to be applied to the event */
  filters: EventFilters;

  /** Set of behavioral filters */
  behavioralFilters?: EventFilters;

  /** Defines how a given entry is grouped with the next one */
  groupingOperator?: FiltersOperator.And | FiltersOperator.Or;

  /** Boolean operator to join filter clauses */
  filtersOperator?: FiltersOperator;

  /** Position of the event if this event specifies exclusion */
  excludeRange?: UnknownObj | {from: number; to: number};

  /** Specifies if exclusion event range will include all collection events */
  excludeAll?: boolean;

  /** Numeric property applied on event */
  property?: PropertyItem;

  /** Specific mathematical aggregation performed on event and its properties */
  aggregationOperator?: MATH_TYPE;

  /** Specific mathematical aggregation performed on a per-user basis */
  aggregationOperatorPerUser?: MATH_TYPE;

  /** if set, the event index label is displayed in toggled state */
  entryIndexLabelToggle?: boolean;

  /** if set, the event value cannot be updated */
  readOnlyEvent?: boolean;

  forward?: number;
  reverse?: number;

  /** for internal use, stores commited values for an entry in stage mode */
  persistedEntry?: QuerySuperEntry;

  /** For internal use, the unique key of this entry row for use with snabbdom */
  entryKey?: number;
}

// TODO(mack): Rename event -> item or cohort/event/filter/segment
export interface QueryEventEntry extends BaseEntry {
  type: ResourceType.Event;
  event: EventItem;
  dateRange?: Partial<DateRange>;
  /** Custom bucketing for HISTOGRAM aggregationOperator  */
  customBucket?: Bucket;
}

export function isEventEntry(entry: QuerySuperEntry): entry is QueryEventEntry {
  return entry.type === ResourceType.Event;
}

export interface FlowsQueryEventEntry extends BaseEntry {
  type: SelectorTypeDefinitions.FlowsEvent;
  event: EventItem;
}

export interface EventPropertyAggregationEntry extends BaseEntry {
  type: SelectorTypeDefinitions.EventPropertyAggregation;
  event: EventItem;
}

export interface UserPropertyAggregationEntry extends BaseEntry {
  type: SelectorTypeDefinitions.UserPropertyAggregation;
  event: EventItem;
}

interface QueryUserEntry extends BaseEntry {
  type: ResourceType.User;
  event: EventItem;
}

export interface QueryCohortEntry extends BaseEntry {
  type: ResourceType.Cohort;
  event: CohortItem;
}

export interface QueryBehaviorEntry extends BaseEntry {
  type: SelectorTypeDefinitions.Behavior;
  event: EventItem;
  dateRange?: Partial<DateRange>;
}

export interface QueryEventLaunch extends BaseEntry {
  type: SelectorTypeDefinitions.EventLaunch;
  event: EventItem;
  dateRange?: Partial<DateRange>;
}

/**
 * An entry which contains multiple child entries.
 *
 * TODO (later): shouldn't extend BaseEntry, but that would require more
 * refactoring of query-entry-section.
 */
export interface QueryGroupedEntry extends BaseEntry {
  type: SelectorTypeDefinitions.GroupedEvent;
  event: QueryEventEntry[];
}

export function isGroupedEntry(entry: QuerySuperEntry): entry is QueryGroupedEntry {
  return entry.type === SelectorTypeDefinitions.GroupedEvent && Array.isArray(entry.event);
}

export interface QueryFilterEntry extends BaseEntry {
  filtersOperator?: FiltersOperator | null;
  type: SelectorTypeDefinitions.Filter;
  event: Partial<PropertyFilter>;
}

export function isFilterEntry(entry: QuerySuperEntry): entry is QueryFilterEntry {
  return entry.type === SelectorTypeDefinitions.Filter;
}

export interface QuerySegmentEntry extends BaseEntry {
  type: SelectorTypeDefinitions.Segment;
  event: Segment[];
  customBucket?: Bucket;
}

export function isSegmentEntry(entry: QuerySuperEntry): entry is QuerySegmentEntry {
  return entry.type === SelectorTypeDefinitions.Segment;
}

/** Union of entry types which may contain a customBucket. */
export type QueryBucketableEntry = QueryEventEntry | QuerySegmentEntry;

/**
 * QB-internal representation of Filter and Breakdown entry types.
 *
 * This is distinct from event/user property aggregations as a QueryMetricEntry.
 */
export type QueryPropertyEntry = QueryFilterEntry | QuerySegmentEntry;
export function isPropertyEntry(entry: QuerySuperEntry): entry is QueryPropertyEntry {
  return isPropertySelectorType(entry.type);
}

/** A single entry representing one cohort, event, property, etc. */
export type QueryMetricEntry =
  | QueryEventEntry
  | FlowsQueryEventEntry
  | QueryUserEntry
  | QueryCohortEntry
  | QueryBehaviorEntry
  | QueryEventLaunch
  | EventPropertyAggregationEntry
  | UserPropertyAggregationEntry;

/** All entry types that can be passed to the query builder. Safe for app code to depend on. */
export type QueryEntry = QueryMetricEntry | QueryGroupedEntry;

/**
 * Query builder internal: all possible types of query entry. App code shouldn't depend on this.
 */
export type QuerySuperEntry = QueryMetricEntry | QueryPropertyEntry | QueryGroupedEntry;

export type EntryItem = QuerySuperEntry['event'];

export enum Action {
  Add = `add`,
  AddFilter = `addFilter`,
  ApplyProperty = `applyProperty`,
  Clone = `clone`,
  Drag = `drag`,
  Edit = `edit`,
  Group = `group`,
  Ungroup = `ungroup`,
  EditFilter = `editFilter`,
  Remove = `remove`,
  RemoveFilter = `removeFilter`,
  Select = `select`,
  SelectDateRange = `selectDateRange`,
  EditFlowSteps = `editFlowSteps`,
}

export enum RowLabelType {
  None = `none`,
  Alpha = `alpha`,
  Numeric = `numeric`,
}

export enum EntryType {
  Event = `event`,
  ExclusionEvent = `exclusion-event`,
}

export const SelectorTypesLabels: {[key in SelectorType]?: string} = {
  // event based types
  [ResourceType.Event]: `event`,
  [SelectorTypeDefinitions.FlowsEvent]: `event`,
  [SelectorTypeDefinitions.Behavior]: `event`,
  [SelectorTypeDefinitions.EventLaunch]: `event`,
  [SelectorTypeDefinitions.GroupedEvent]: `event`,
  [SelectorTypeDefinitions.EventPropertyAggregation]: `event`,
  [SelectorTypeDefinitions.UserPropertyAggregation]: `user`,
  [ResourceType.Cohort]: `cohort`,
  [SelectorTypeDefinitions.Behavior]: `behavior`,
  [ResourceType.User]: `user profile`,
};

const EMPTY_EVENT: EventItem = Object.freeze({
  id: null,
  custom: false,
  label: ``,
  value: null,
});

export const EMPTY_EVENT_ENTRY = Object.freeze({
  aggregationOperator: PRIMATIVE_MATH_TYPES.TOTAL,
  aggregationOperatorPerUser: null,
  event: EMPTY_EVENT,
  filters: [],
  filtersOperator: FiltersOperator.And,
  property: null,
  type: ResourceType.Event,
}) as QueryEventEntry;

export const EMPTY_EXCLUSION_EVENT_ENTRY: QueryEventEntry = Object.freeze({
  ...EMPTY_EVENT_ENTRY,
  excludeAll: true,
  excludeRange: {},
});

/**
 * Template variables for labels and descriptions for the selected
 * data group
 * @example group: user|Account
 * @example pluralGroup: users|Account(s)
 * @example pluralGroupTitle: Users|Account(s)
 */
export enum AggregationGroupLabel {
  Group = `group`,
  PluralGroup = `pluralGroup`,
  PluralGroupTitle = `pluralGroupTitle`,
}

export type AggregationLabelParams = {[key in AggregationGroupLabel]: string};

export type SelectorTypeAggregationLabel = string | ((params: AggregationLabelParams) => string);

export interface SelectorTypeAggregation {
  selectorType: QueryMetricEntry['type'];

  /** Defines across-users aggregations */
  operator?: MATH_TYPE;

  /** Defines per-user aggregations */
  perUserOperator?: MATH_TYPE;

  description: SelectorTypeAggregationLabel;
  shortLabel?: SelectorTypeAggregationLabel;
  subAggregations?: SelectorTypeAggregationItem[];

  /** TODO(pablo): needed until we converge to a single aggregations experience */
  order?: number;

  /**
   * Describes legacy labels in EVENT_MATH_LABELS and PROPERTY_MATH_LABELS
   * TODO(pablo): remove once internal testing is completed for new metrics mode
   */
  shortName?: string;
}

// label as functions are evaluated by formatDataGroupLabel in iron/common/widgets/query-entry-section
export interface SelectorTypeAggregationItem extends Omit<SectionItem<SelectorTypeAggregation>, 'label'> {
  label: string | ((...args: any[]) => string);
}
export interface SelectorTypeAggregationSection extends Omit<Section<SelectorTypeAggregationItem>, 'label' | 'items'> {
  label?: string | ((...args: any[]) => string);
  items?: SelectorTypeAggregationItem[];
}

/** When using Add grouped event, how to add the new row. */
export enum GroupEventBehaviors {
  /** Grouped events are disabled. `Add grouped event` is not allowed. */
  None = `none`,
  /** Adding a grouped event should append it to the current entries list. */
  Append = `append`,
  /** Adding a grouped event should create a new group at the current entry. */
  Nest = `nest`,
}

/** When a row-label-prefix is passed, how to display it and handle interactions. */
export enum RowPrefixBehaviors {
  /** Don't show a row prefix at all. */
  None = `none`,
  /** Show the prefix on every row. */
  EveryRow = `every`,
  /** Show the prefix only on the first row. */
  FirstOnly = `first`,
}

/** Identifier for a possibly nested entry. Ex: [0,2] = first entry, third child. */
export type RowAddress = [number] | [number, number];

export enum EntryPart {
  Aggregator = `aggregator`,
  BehavioralFilters = `behavioralFilters`,
  CustomBucketing = `customBucketing`,
  Filters = `filters`,
  NumericProperty = `numericProperty`,
  SubAggregator = `subAggregator`,
  FilterValue = `filterValue`,
  Value = `value`,
}

export type EntrySectionUpdateEvent = CustomEvent<{
  entries: QuerySuperEntry[];
  updatedEntry: QuerySuperEntry;
  queryReason?: QueryReason;
}>;

export type UpdateStatus = {
  dispatchedUpdate: boolean;
};
